// ============================================================
// Core site bundle — loaded on every page via _Layout.cshtml
// Includes: Bootstrap, KnockoutJS, Luxon, utilities
// Replaces the WebOptimizer site.js bundle.
// ============================================================

import { userConfigManager } from '../shared/user-config-manager';
import { getJwt } from '../shared/jwt-client';
import * as userStoreClient from '../shared/user-store-client';
import { readConfig } from '../shared/config';
import { flushPendingPicks } from '../shared/pending-picks';
import { hydrateMissingLeagueSaveStrings } from './site-hydration';

// -- Bootstrap 5 JS (no jQuery dependency) --
import * as bootstrap from 'bootstrap';
(window as any).bootstrap = bootstrap;

// -- jQuery shim: $.proxy used in KO data-bind expressions in .cshtml views --
// $.proxy(fn, context) is equivalent to fn.bind(context).
// This shim allows existing views to work without jQuery.
(window as any).$ = (window as any).$ || {};
(window as any).$.proxy = function(fn: Function, context: any, ...args: any[]) {
  return fn.bind(context, ...args);
};

// -- KnockoutJS --
import * as ko from 'knockout';
(window as any).ko = ko;

// -- Luxon --
import { DateTime } from 'luxon';
(window as any).luxon = { DateTime };

// -- LZ-String compression (replaces old lzw.js / LZString global) --
import LZString from 'lz-string';
(window as any).LZString = LZString;

// -- Toast notifications (jQuery-free replacement for toastr) --
import { ppToastr } from '../shared/pp-toastr';
(window as any).toastr = ppToastr;

// -- html2canvas --
import html2canvas from 'html2canvas';
(window as any).html2canvas = html2canvas;

// -- Shared helpers (mapDictionaryToArray, rankSort, divisionRankSort) --
// Exposed on window.Helpers for KO template bindings in .cshtml files
import * as Helpers from '../shared/helpers';
(window as any).Helpers = Helpers;

// ============================================================
// KO Observable Extensions
// ============================================================

(ko.observable as any).fn.increment = function(this: KnockoutObservable<number>, value?: number) {
  this(this() + (value || 1));
};

(ko.subscribable as any).fn.subscribeChanged = function(
  this: KnockoutSubscribable<any>,
  callback: (newValue: any, oldValue: any) => void
) {
  let oldValue: any;
  this.subscribe((_oldValue: any) => { oldValue = _oldValue; }, null, 'beforeChange');
  this.subscribe((newValue: any) => { callback(newValue, oldValue); });
};

(ko.observable as any).fn.withPausing = function(this: any) {
  this.notifySubscribers = function(this: any) {
    if (!this.pauseNotifications) {
      ko.subscribable.fn.notifySubscribers.apply(this, arguments as any);
    }
  };
  this.sneakyUpdate = function(this: any, newValue: any) {
    this.pauseNotifications = true;
    this(newValue);
    this.pauseNotifications = false;
  };
  return this;
};

// ============================================================
// KO Binding Handlers
// ============================================================

ko.bindingHandlers.preventBubble = {
  init(element: Element, valueAccessor: () => any) {
    const eventName = ko.utils.unwrapObservable(valueAccessor());
    ko.utils.registerEventHandler(element, eventName, (event: Event) => {
      (event as any).cancelBubble = true;
      event.stopPropagation?.();
    });
  },
};

ko.bindingHandlers.cssEnable = {
  init(element: HTMLElement, valueAccessor: () => any) {
    element.classList.toggle('disabled', !ko.unwrap(valueAccessor()));
  },
  update(element: HTMLElement, valueAccessor: () => any) {
    element.classList.toggle('disabled', !ko.unwrap(valueAccessor()));
  },
};

/**
 * Map a Luxon-friendly format string (MM/DD/YYYY or MM/DD/YYYY h:mm A)
 * to a Luxon toFormat token string.
 */
function toLuxonFormat(fmt: string): string {
  return fmt
    .replace('MM', 'LL')
    .replace('DD', 'dd')
    .replace('YYYY', 'yyyy')
    .replace('h:mm A', 'h:mm a');
}

function applyDateFormat(element: HTMLElement, valueAccessor: () => any, format: string): void {
  let value = ko.unwrap(valueAccessor());
  if (ko.isObservable(value)) { value = ko.unwrap(value); }

  // Guard: Luxon DateTime with year=0 is invalid — skip rendering
  if (DateTime.isDateTime(value) && (value as DateTime).year === 0) return;

  let formatted: string | undefined;
  const luxonFmt = toLuxonFormat(format);
  const includesTime = format.includes('h:mm');

  if (DateTime.isDateTime(value)) {
    const dt = value as DateTime;
    const effective = includesTime ? dt : dt.startOf('day');
    formatted = effective.toFormat(luxonFmt).replace(' am', ' AM').replace(' pm', ' PM');
  } else if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value as Date);
    const effective = includesTime ? dt : dt.startOf('day');
    formatted = effective.toFormat(luxonFmt).replace(' am', ' AM').replace(' pm', ' PM');
  }
  if (formatted !== undefined) {
    element.innerHTML = formatted;
    (element as HTMLInputElement).value = formatted;
    element.textContent = formatted;
  }
}

ko.bindingHandlers.defaultDateFormat = {
  init: (el: any, va: any) => applyDateFormat(el, va, 'MM/DD/YYYY'),
  update: (el: any, va: any) => applyDateFormat(el, va, 'MM/DD/YYYY'),
};

ko.bindingHandlers.defaultDateTimeFormat = {
  init: (el: any, va: any) => applyDateFormat(el, va, 'MM/DD/YYYY h:mm A'),
  update: (el: any, va: any) => applyDateFormat(el, va, 'MM/DD/YYYY h:mm A'),
};

const tooltipKey = '__bsTooltipInstance';

ko.bindingHandlers.tooltip = {
  init(element: HTMLElement) {
    ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
      const instance = (element as any)[tooltipKey] as bootstrap.Tooltip | undefined;
      if (instance) {
        instance.dispose();
        delete (element as any)[tooltipKey];
      }
    });
  },
  update(element: HTMLElement, valueAccessor: () => any) {
    const raw = ko.unwrap(valueAccessor());
    const opts: Record<string, any> = typeof raw === 'object' ? ko.toJS(raw) : { title: String(raw) };

    const title = opts.title != null ? String(opts.title) : '';
    const bsOptions: Partial<bootstrap.Tooltip.Options> = {
      title,
      html: !!opts.html,
      trigger: 'hover',
    };
    if (opts.container) {
      bsOptions.container = opts.container;
    }
    if (opts.placement) {
      bsOptions.placement = opts.placement;
    }

    const existing = (element as any)[tooltipKey] as bootstrap.Tooltip | undefined;
    if (existing) {
      delete (element as any)[tooltipKey];
      existing.dispose();
    }
    if (title && element.isConnected) {
      (element as any)[tooltipKey] = new bootstrap.Tooltip(element, bsOptions);
    }
  },
};

// ============================================================
// Theme (nightmode)
// Read from UserConfig.NightmodeEnabled (legacy storage key) for
// backwards-compatibility with existing user preferences.
// ============================================================

const USER_CONFIG_KEY = 'UserConfig';
const NIGHTMODE_KEY = 'nightmode';

function isNightmodeEnabled(): boolean {
  // Prefer the standalone key (written by toggleTheme going forward).
  const standalone = localStorage.getItem(NIGHTMODE_KEY);
  if (standalone !== null) return standalone === 'true';
  // Fall back to legacy UserConfig key so existing preferences are preserved.
  try {
    const raw = localStorage.getItem(USER_CONFIG_KEY);
    if (raw) {
      const cfg = JSON.parse(raw);
      return !!cfg?.NightmodeEnabled;
    }
  } catch { /* ignore parse errors */ }
  return false;
}

export function initTheme(): void {
  if (isNightmodeEnabled()) {
    document.body.classList.add('nightmode');
  }
}

export function toggleTheme(): void {
  const enabled = document.body.classList.toggle('nightmode');
  localStorage.setItem(NIGHTMODE_KEY, String(enabled));
  // Keep legacy UserConfig in sync so old serialised pages stay consistent.
  try {
    const raw = localStorage.getItem(USER_CONFIG_KEY);
    const cfg = raw ? JSON.parse(raw) : {};
    cfg.NightmodeEnabled = enabled;
    localStorage.setItem(USER_CONFIG_KEY, JSON.stringify(cfg));
  } catch { /* ignore */ }
}

// Init immediately (body exists when site.js loads at bottom of <body>)
initTheme();

// ============================================================
// GA stub (some browsers/extensions block GA, breaking downstream code)
// ============================================================
(window as any).ga = (window as any).ga || function() {};

// ============================================================
// DOM Ready
// ============================================================

document.addEventListener('DOMContentLoaded', () => {
  // Contact email obfuscation
  document.querySelectorAll('.ctc').forEach(el => { (el as HTMLAnchorElement).href = 'mailto:ray@playoffpredictors.com'; });
  document.querySelectorAll('.dpo').forEach(el => { (el as HTMLAnchorElement).href = 'mailto:dpo@playoffpredictors.com'; });

  // Mobile dropdown fix
  document.addEventListener('touchstart', (event) => {
    if ((event.target as Element).closest('.dropdown-submenu > a')) {
      event.preventDefault();
    }
  });
  document.addEventListener('touchend', (event) => {
    if ((event.target as Element).closest('.dropdown-submenu > a')) {
      event.preventDefault();
    }
  });

  // Nightmode toggle
  document.addEventListener('click', (event) => {
    if ((event.target as Element).closest('#nightmode')) {
      toggleTheme();
    }
  });

  // Debug mode guard: only the dfpkey URL-param removal is retained.
  // isDebugMode is now stored in UserConfig.IsDebugMode (server-synced).
  setInterval(() => {
    if (userConfigManager.UserConfig.IsDebugMode) {
      if (window.location.host !== 'localhost' && window.location.host !== 'green.playoffpredictors.com') {
        // Debug mode should only be active on whitelisted hosts — clear it.
        userConfigManager.UserConfig.IsDebugMode = false;
        userConfigManager.saveStorage();
        location.reload();
      }
    }
    if (new URLSearchParams(window.location.search).get('dfpkey')) {
      if (window.location.host !== 'localhost' && window.location.host !== 'green.playoffpredictors.com') {
        location.replace('//' + location.host);
      }
    }
  }, 2500);

  // ── Auth hydration ────────────────────────────────────────────────────────
  // On every page load when the user is authenticated:
  // 1. Warm the JWT cache so debounced pick PUTs have a token ready.
  // 2. Hydrate UserConfig from the server blob (server wins per-key).
  // 3. Hydrate missing local league saveStrings from the server blob.
  //    Existing local saveStrings are preserved and are only written back by
  //    the debounced picker-save path after an actual local change.
  if (readConfig().isAuthenticated) {
    void getJwt();

    void (async () => {
      await userConfigManager.hydrateFromServer();

      let remoteLeagues: Record<string, userStoreClient.RemoteLeague>;
      try {
        remoteLeagues = await userStoreClient.getLeagues();
      } catch {
        return;
      }

      hydrateMissingLeagueSaveStrings(remoteLeagues);
    })();
  }

  // ── Flush pending pick PUTs on page unload ────────────────────────────────
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPendingPicks();
    }
  });
  window.addEventListener('pagehide', () => {
    flushPendingPicks();
  });
});
